import * as R from 'ramda';
import React from 'react';

import { JobDetailContext } from '@/contexts';
import {
  JobCompanyUserContextDocument,
  useFollowCompanyMutation,
  useJobCompanyUserContextQuery,
  useUnfollowCompanyMutation,
} from '@/graphql/generated';

type UseToggleFollowCompanyResult = {
  readonly isFollowing: boolean;
  readonly onToggleFollow: () => void;
};

export const useToggleFollowCompany = (followedId: string): UseToggleFollowCompanyResult => {
  const { slug } = React.useContext(JobDetailContext);
  const { data } = useJobCompanyUserContextQuery({ variables: { slug } });

  const handleCacheUpdate = (proxy): void => {
    proxy.writeQuery({
      query: JobCompanyUserContextDocument,
      variables: { slug },
      data: R.evolve({ job: { companyInfo: { company: { userContext: { followState: { isFollowing: R.not } } } } } })(
        proxy.readQuery({ query: JobCompanyUserContextDocument, variables: { slug } })
      ),
    });
  };

  const [onFollowCompany] = useFollowCompanyMutation({
    variables: { input: { followedId } },
    optimisticResponse: {
      __typename: 'Mutation',
      companyFollow: { error: null, __typename: 'FollowersMutationResult' },
    },
    update: handleCacheUpdate,
  });
  const [onUnfollowCompany] = useUnfollowCompanyMutation({
    variables: { input: { followedId } },
    optimisticResponse: {
      __typename: 'Mutation',
      companyUnfollow: { error: null, __typename: 'FollowersMutationResult' },
    },
    update: handleCacheUpdate,
  });

  const isFollowing = R.pathOr<boolean>(
    false,
    ['job', 'companyInfo', 'company', 'userContext', 'followState', 'isFollowing'],
    data
  );
  const onToggleFollow = isFollowing ? onUnfollowCompany : onFollowCompany;

  return { isFollowing, onToggleFollow };
};
